import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { apiClient, ApiError, NotAuthenticatedError } from "../api/client.js";
import { useMe } from "../api/use-me.js";
import { AssetDetailModal } from "../components/asset-detail-modal.js";
import { AssetFolderView } from "../components/asset-folder-view.js";
import { AssetGrid } from "../components/asset-grid.js";
import { AssetList } from "../components/asset-list.js";
import { ConfirmDialog } from "../components/confirm-dialog.js";
import { FilterPanel } from "../components/filter-panel.js";
import { buildSearch, parseFilters, type FilterState } from "../components/filter-state.js";
import {
  DEFAULT_GRID_ASPECT,
  DEFAULT_GRID_ZOOM,
  GRID_ASPECTS,
  GridAspectToggle,
  GridZoomSlider,
  type GridAspect,
  type GridZoom,
} from "../components/grid-zoom-slider.js";
import { PathBreadcrumb } from "../components/path-breadcrumb.js";
import {
  SelectionToolbar,
  type SelectionAction,
} from "../components/selection-toolbar.js";
import type { Asset } from "../api/types.js";

// 1.D.5 Selaa-naekyma + 1.D.6 faceted filter.
// Filtterit ovat URL-paramien takana → linkki on jaettavissa, refresh
// sailyttaa tilan. Project-listaus tulee /me-vastauksesta jolloin manager
// nakee kaikki ja muut vain omat + jaetut.

type ViewMode = "list" | "grid" | "folder";

export function Selaa() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const filters = useMemo<FilterState>(() => parseFilters(search), [search]);

  const [assets, setAssets] = useState<Asset[]>([]);
  const { me } = useMe();
  const projects = me?.user.projects ?? [];
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauthenticated, setUnauthenticated] = useState(false);
  const [view, setView] = useState<ViewMode>("grid");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // UM.8 — grid zoom + aspect (persistoidaan localStorage:iin, jotta valinta
  // sailyy refresh:n yli ja eri valintojen valilla)
  const [gridZoom, setGridZoom] = useState<GridZoom>(() => {
    if (typeof window === "undefined") return DEFAULT_GRID_ZOOM;
    const raw = window.localStorage.getItem("kratti:grid-zoom");
    const parsed = raw === null ? NaN : parseInt(raw, 10);
    if (parsed >= 0 && parsed <= 4) return parsed as GridZoom;
    return DEFAULT_GRID_ZOOM;
  });
  useEffect(() => {
    window.localStorage.setItem("kratti:grid-zoom", String(gridZoom));
  }, [gridZoom]);

  const [gridAspect, setGridAspect] = useState<GridAspect>(() => {
    if (typeof window === "undefined") return DEFAULT_GRID_ASPECT;
    const raw = window.localStorage.getItem("kratti:grid-aspect");
    if (raw && (GRID_ASPECTS as readonly string[]).includes(raw)) {
      return raw as GridAspect;
    }
    return DEFAULT_GRID_ASPECT;
  });
  useEffect(() => {
    window.localStorage.setItem("kratti:grid-aspect", gridAspect);
  }, [gridAspect]);

  // 2.D.3 multi-select tila
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);

  // Lataa assetit aina kun filtterit muuttuvat.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUnauthenticated(false);
    apiClient
      .listAssets({ limit: 100, ...filters })
      .then((res) => {
        if (cancelled) return;
        setAssets(res.assets);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) {
          setUnauthenticated(true);
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  function applyFilters(next: FilterState) {
    const qs = buildSearch(next);
    navigate(qs ? `/selaa?${qs}` : "/selaa");
  }

  // 2.D.3 — toggle-select per asset
  const toggleSelect = useCallback((asset: Asset) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(asset.id)) next.delete(asset.id);
      else next.add(asset.id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // 2.D.5 — bulk-action handler
  const runBulkAction = useCallback(
    async (action: SelectionAction) => {
      if (selectedIds.size === 0) return;
      const ids = Array.from(selectedIds);

      // Delete vaatii vahvistuksen
      if (action.type === "delete") {
        setConfirmDelete(true);
        return;
      }

      setBulkProcessing(true);
      setBulkMessage(null);
      try {
        const res = await apiClient.bulkAssets({ ids, action });
        const successCount = res.succeeded.length;
        const failCount = res.failed.length;
        setBulkMessage(
          failCount === 0
            ? `Onnistui ${successCount}/${ids.length}`
            : `Onnistui ${successCount}/${ids.length}, ${failCount} epaonnistui`
        );
        // Lataa lista uudelleen jotta muutokset nakyvat
        const list = await apiClient.listAssets({ limit: 100, ...filters });
        setAssets(list.assets);
      } catch (err) {
        setBulkMessage(err instanceof Error ? err.message : String(err));
      } finally {
        setBulkProcessing(false);
      }
    },
    [selectedIds, filters]
  );

  const confirmDeleteAction = useCallback(async () => {
    setBulkProcessing(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await apiClient.bulkAssets({ ids, action: { type: "delete" } });
      setBulkMessage(
        res.failed.length === 0
          ? `Poistettu ${res.succeeded.length}/${ids.length}`
          : `Poistettu ${res.succeeded.length}/${ids.length}, ${res.failed.length} epaonnistui`
      );
      // Poista UI:lta heti
      setAssets((prev) => prev.filter((a) => !res.succeeded.includes(a.id)));
      setSelectedIds(new Set());
    } catch (err) {
      setBulkMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBulkProcessing(false);
      setConfirmDelete(false);
    }
  }, [selectedIds]);

  // ESC tyhjentaa valinnan
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") clearSelection();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedIds.size, clearSelection]);

  return (
    <section className="mx-auto max-w-6xl px-6 py-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Selaa</h2>
          <p className="mt-1 text-sm text-slate-500">Asset-detail modal tulee 1.D.7:ssa.</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {view !== "list" && (
            <>
              <GridZoomSlider value={gridZoom} onChange={setGridZoom} />
              <GridAspectToggle value={gridAspect} onChange={setGridAspect} />
            </>
          )}
          <ViewToggle value={view} onChange={setView} />
        </div>
      </header>

      <div className="mb-6">
        <FilterPanel filters={filters} projects={projects} onChange={applyFilters} />
      </div>

      {filters.path && (
        <div className="mb-4">
          <PathBreadcrumb
            path={filters.path}
            onChange={(next) => {
              const updated: FilterState = { ...filters };
              if (next === null) delete updated.path;
              else updated.path = next;
              applyFilters(updated);
            }}
          />
        </div>
      )}

      {/* 2.D.5 — bulk-actions toolbar nakyy kun valittuja */}
      <SelectionToolbar
        count={selectedIds.size}
        isProcessing={bulkProcessing}
        onAction={runBulkAction}
        onClear={clearSelection}
      />

      {bulkMessage && (
        <div className="mb-4 rounded-md border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm text-slate-200">
          {bulkMessage}
        </div>
      )}

      {unauthenticated && (
        <div className="rounded-md border border-amber-700 bg-amber-950/40 px-4 py-3 text-sm text-amber-200">
          Et ole kirjautunut sisaan.{" "}
          <a href="/login" className="underline hover:text-amber-100">
            Kirjaudu
          </a>{" "}
          jatkaaksesi.
        </div>
      )}
      {error && !unauthenticated && (
        <div className="rounded-md border border-red-700 bg-red-950/40 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}
      {isLoading && !unauthenticated && !error && (
        <p className="text-slate-500">Ladataan assetteja…</p>
      )}
      {!unauthenticated &&
        !error &&
        view === "folder" && (
          <AssetFolderView
            path={filters.path ?? "/"}
            filterQuery={(() => {
              const { path: _omit, ...rest } = filters;
              void _omit;
              return rest;
            })()}
            onPathChange={(next) => {
              const updated: FilterState = { ...filters };
              if (next === "/") delete updated.path;
              else updated.path = next;
              applyFilters(updated);
            }}
            onSelect={(a) => setSelectedId(a.id)}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            zoom={gridZoom}
            aspect={gridAspect}
          />
        )}
      {!isLoading &&
        !unauthenticated &&
        !error &&
        view !== "folder" &&
        (view === "list" ? (
          <AssetList
            assets={assets}
            onSelect={(a) => setSelectedId(a.id)}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
          />
        ) : (
          <AssetGrid
            assets={assets}
            onSelect={(a) => setSelectedId(a.id)}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            zoom={gridZoom}
            aspect={gridAspect}
          />
        ))}

      <AssetDetailModal assetId={selectedId} onClose={() => setSelectedId(null)} />

      <ConfirmDialog
        open={confirmDelete}
        title="Poista valitut?"
        message={
          <p>
            Poistetaan <strong>{selectedIds.size}</strong> assettia. Arkistosnapshot
            sailyy — voit palauttaa myohemmin manuaalisesti.
          </p>
        }
        confirmLabel="Poista"
        destructive
        isProcessing={bulkProcessing}
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete(false)}
      />
    </section>
  );
}

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const btn = (mode: ViewMode, label: string) => {
    const active = value === mode;
    const cls = active
      ? "rounded-md bg-slate-100 px-3 py-1.5 text-sm text-slate-900"
      : "rounded-md px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800";
    return (
      <button type="button" onClick={() => onChange(mode)} className={cls} aria-pressed={active}>
        {label}
      </button>
    );
  };
  return (
    <div className="inline-flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900 p-1">
      {btn("grid", "Grid")}
      {btn("list", "Lista")}
      {btn("folder", "Kansiot")}
    </div>
  );
}
